/**
 * Foods — the library, and the tab that replaces Add.
 *
 * `Add` left the navigation because logging no longer has a page: it happens in
 * a sheet raised over the day it writes into. What that freed was a slot, and
 * what went into it is the thing the app never had a home for — the foods
 * themselves.
 *
 * They were scattered across three screens that had nothing to do with each
 * other. The starred list and the recipe editor lived on "You", beside the
 * timezone. The by-hand form only appeared after a search that found nothing,
 * so reaching it meant knowing in advance that the search would fail. And the
 * product correction could only be opened through a *logged entry*, which meant
 * fixing a misread label required first eating something.
 *
 * All three are the same act — creating and correcting the rows the rest of the
 * app logs — so they are one screen.
 *
 * **No `+` on any row.** Logging needs a meal to land in and this screen has
 * none; offering one here would have to invent a meal, and guessing which is
 * how food ends up in the wrong row. It is stated at the bottom rather than
 * left as an absence, because "why can't I add from here" is the first question
 * this screen asks of anybody who reads it as a list of food.
 *
 * The row's provenance is stated for a harder reason: it decides what editing
 * is allowed to do. A barcode row is shared, so a fix to it is a fix for
 * everyone and is audited; a private guess forks. The editor asks which, and it
 * can only ask sensibly if the list already said where the numbers came from.
 */

import {
  RECENT_PAGE_SIZE,
  useFavourites,
  useFoodSearch,
  useRecentPages,
  useRecipes,
  useSetFavourite,
  type FoodItem,
  type RecentSort,
} from "@calorio/core";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useCalorio } from "../app/providers";

type Filter = "saved" | "mine" | "meals" | "recent";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "saved", label: "Saved" },
  { id: "mine", label: "Mine" },
  { id: "meals", label: "Meals" },
  { id: "recent", label: "Recently used" },
];

/**
 * Where a food's numbers came from, in words.
 *
 * The same four sources the scan confirm names, said the same way, because a
 * user who learns "from a barcode" on one screen should not have to learn
 * "off" on another. `generic` is the seeded reference data.
 */
const SOURCE_COPY: Record<string, string> = {
  off: "from a barcode",
  label_ocr: "read off a label",
  user: "entered by hand",
  generic: "reference data",
};

/**
 * The four figures the recent list can be ranked by, and how to read them off.
 *
 * This is the one list in the app worth asking a nutritional question of. A
 * search result is the whole database, most of which nobody here has ever
 * eaten; a day is one day. What you have logged is what you actually eat, so
 * "which of these is the protein one" has an answer here and nowhere else.
 *
 * Per 100 basis units throughout — the number stored on the food, which is what
 * ranks foods against each other. How much protein a food has *given* you is a
 * question about portions and belongs to Review.
 */
const MACROS: {
  id: Exclude<RecentSort, "recent">;
  /** On the sort control, where there is room for a word. */
  label: string;
  /** On the row, where there are four of them and there is not. */
  short: string;
  unit: string;
  of: (food: FoodItem) => number;
}[] = [
  { id: "kcal", label: "Calories", short: "kcal", unit: "kcal", of: (f) => f.kcal_per_100 },
  { id: "protein", label: "Protein", short: "P", unit: "g", of: (f) => f.protein_g_per_100 },
  { id: "carbs", label: "Carbs", short: "C", unit: "g", of: (f) => f.carbs_g_per_100 },
  { id: "fat", label: "Fat", short: "F", unit: "g", of: (f) => f.fat_g_per_100 },
];

/** Whole calories, one decimal for grams — and no trailing ".0" on a round one. */
const figure = (value: number, unit: string) =>
  unit === "kcal" ? String(Math.round(value)) : String(Math.round(value * 10) / 10);

export function Foods() {
  const ctx = useCalorio();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<Filter>("saved");
  const [query, setQuery] = useState("");
  /**
   * How the recent list is ranked. `"recent"` is the clock it always was.
   *
   * Kept for both list filters that read from it, because "Mine" is Recent with
   * the shared rows taken out — the same foods, the same question, so it sorts
   * the same way rather than snapping back to the clock when you switch to it.
   */
  const [sort, setSort] = useState<RecentSort>("recent");

  const favourites = useFavourites(ctx);
  /**
   * Paged, because the ask is "everything I have eaten", not "the last twenty".
   *
   * This used to take the first page of `/v1/foods/recent` and stop, which was
   * defensible while the list was a fixed panel and indefensible the moment it
   * could be sorted: the highest-protein food you eat is very often not in the
   * twenty most recent, so a sort over one page ranks the wrong set and reads
   * exactly like ranking the right one.
   *
   * No meal — this screen is not open on one, so the seating plan has nothing
   * to say here and the clock is the honest default.
   */
  const recent = useRecentPages(ctx, null, RECENT_PAGE_SIZE, sort);
  const recipes = useRecipes(ctx);
  const setFavourite = useSetFavourite(ctx);

  /** The pages flattened, since a list does not care where a page ended. */
  const eaten = useMemo(
    () => recent.data?.pages.flatMap((page) => page.items.map((item) => item.food)),
    [recent.data],
  );

  /**
   * Search runs across the whole cache, not across the filtered list.
   *
   * Filtering a list of twenty starred foods by substring is a worse answer
   * than the search that already exists: somebody typing a name is looking for
   * a food, and the useful reply includes the one they have not starred yet.
   * The filter pills go quiet while a query is in the box rather than
   * pretending to narrow something they are not narrowing.
   */
  const searching = query.trim().length >= 2;
  const results = useFoodSearch(ctx, query);

  const starred = new Set(favourites.data?.map((f: FoodItem) => f.id));

  /**
   * Whether the list on screen is the one derived from the log.
   *
   * The sort control and the figures beside each row only make sense over that
   * list. Offering them above a search result would rank the database, and
   * above the starred list would rank a shortlist somebody curated by hand.
   */
  const ranking = !searching && (filter === "recent" || filter === "mine");

  const listed: FoodItem[] | undefined = searching
    ? results.data
    : filter === "saved"
      ? favourites.data
      : filter === "mine"
        ? eaten?.filter((food) => !food.shared)
        : // "Meals" is the composed recipes, which are their own section below.
          // The pill scrolls the list out of the way rather than filtering it,
          // because a recipe is not a food row and rendering it as one would
          // lose the two facts that make it one — what it makes and what is in
          // it.
          filter === "meals"
          ? []
          : eaten;

  /**
   * What an empty list means, and the one act that fills it.
   *
   * Each filter is empty for its own reason, so each names its own next step
   * rather than sharing a sentence that fits none of them. A bare "Nothing
   * logged yet." is true and useless: it describes the state and leaves the
   * reader to guess which of this screen's four doors is the one they want.
   *
   * Starring is the odd one out. It has no page — you star a food while you are
   * logging it — so the act it names is logging, and the link goes to the day.
   */
  const empty: { note: string; to: string; act: string } =
    filter === "saved"
      ? {
          note:
            "Nothing starred yet. Star a food while you are logging it and it lands here, " +
            "ready to log again without a search.",
          to: "/",
          act: "Log something and star it",
        }
      : filter === "mine"
        ? {
            note:
              "Nothing you typed in yourself yet. A food you enter by hand lives here — your " +
              "numbers, yours alone, editable whenever the pack changes.",
            to: "/foods/new",
            act: "Enter a food by hand",
          }
        : {
            note:
              "Nothing logged yet. Everything you eat collects here, and once there is a " +
              "handful you can rank it by calories or protein rather than by the clock.",
            to: "/foods/scan",
            act: "Scan a barcode",
          };

  return (
    <div className="foods">
      <header className="foods__head">
        <h1 className="foods__title">Foods</h1>
        {/* Scan carries the border and "New food" does not, for the reason day
            one gives the same emphasis to the same choice: it is the fast path
            and the one the app is for. Typing a food in is what you do when
            there is nothing to read. */}
        <div className="foods__actions">
          <Link to="/foods/scan" className="foods__new">
            Scan
          </Link>
          <Link to="/foods/new" className="foods__new-hand">
            New food
          </Link>
        </div>
      </header>

      <input
        className="foods__search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search your foods"
        aria-label="Search your foods"
      />

      {!searching && (
        <div className="foods__filters" role="tablist">
          {FILTERS.map((option) => (
            <button
              key={option.id}
              role="tab"
              aria-selected={filter === option.id}
              className="foods__filter"
              onClick={() => setFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Ranked by a figure, or by the clock. The clock is a *choice* here
          rather than the absence of one, which is why it sits in the same row
          as the four figures instead of being the state you return to by
          switching a sort off. */}
      {ranking && (
        <div className="foods__sorts" role="tablist" aria-label="Sort these foods by">
          {[{ id: "recent" as RecentSort, label: "Recent" }, ...MACROS].map((option) => (
            <button
              key={option.id}
              role="tab"
              aria-selected={sort === option.id}
              className="foods__sort"
              onClick={() => setSort(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {!listed && <p className="muted">Loading…</p>}
      {listed?.length === 0 &&
        filter !== "meals" &&
        (searching ? (
          /* A search that found nothing is not an empty account. The row may
             well exist under another spelling, so the next step is the box the
             reader is already typing in — there is no act to name here. */
          <p className="muted">Nothing matched.</p>
        ) : (
          <div className="card stack" style={{ gap: "var(--sp-2)" }}>
            <p className="manual__note">{empty.note}</p>
            <p className="manual__note">
              <Link to={empty.to} className="muted">
                {empty.act} →
              </Link>
            </p>
          </div>
        ))}

      {listed?.map((food) => {
        const on = starred.has(food.id);
        return (
          <div className="entry" key={food.id}>
            <button
              className="entry__star"
              aria-pressed={on}
              aria-label={on ? `Unstar ${food.name}` : `Star ${food.name}`}
              onClick={() => setFavourite.mutate({ foodId: food.id, favourite: !on })}
            >
              {on ? "★" : "☆"}
            </button>
            <button
              className="entry__body entry__open"
              onClick={() => navigate(`/foods/${food.id}`)}
            >
              <div className="entry__name">
                {food.brand && <span className="muted">{food.brand} </span>}
                {food.name}
              </div>
              {/* Ranked lists print all four figures, because a list ordered by
                  protein that shows only calories asks the reader to take the
                  order on trust. The one being sorted by is marked rather than
                  moved: the columns staying put is what lets you compare two
                  sorts of the same list. */}
              {ranking ? (
                <>
                  <div className="entry__meta">
                    {SOURCE_COPY[food.source] ?? "entered by hand"} · per 100 {food.basis_unit}
                  </div>
                  <div className="entry__macros num">
                    {MACROS.map((macro) => (
                      <span
                        key={macro.id}
                        className={`entry__macro${sort === macro.id ? " entry__macro--on" : ""}`}
                      >
                        {/* Calories read "355 kcal"; grams read "P 7.5", with
                            the unit carried by the "per 100 g" above rather
                            than repeated four times on every row. */}
                        {macro.unit === "kcal"
                          ? `${figure(macro.of(food), macro.unit)} ${macro.short}`
                          : `${macro.short} ${figure(macro.of(food), macro.unit)}`}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <div className="entry__meta num">
                  {Math.round(food.kcal_per_100)} kcal / 100 {food.basis_unit} ·{" "}
                  {SOURCE_COPY[food.source] ?? "entered by hand"}
                </div>
              )}
            </button>
            <span className="foods__chev" aria-hidden="true">
              ›
            </span>
          </div>
        );
      })}

      {/* The list does not end where the page does, and saying so is the whole
          difference between "everything you have eaten" and "the last twenty".
          A button rather than an observer: on a screen with a sort control,
          rows arriving under the fold while you are reading the top of a
          ranking is the list moving without being asked. */}
      {ranking && recent.hasNextPage && (
        <button
          className="foods__more"
          onClick={() => void recent.fetchNextPage()}
          disabled={recent.isFetchingNextPage}
        >
          {recent.isFetchingNextPage ? "Loading…" : "Show more"}
        </button>
      )}

      {/* Composed meals. They log as one portion instead of five ingredients,
          and the editor that builds them is the only thing that can — which is
          why "compose one" is a link and not an empty-state consolation. */}
      {!searching && (
        <section className="foods__meals">
          <div className="foods__meals-head">
            <h2 className="usuals__label">Your meals</h2>
            <Link to="/recipes/new" className="foods__new-meal">
              Compose one
            </Link>
          </div>

          {!recipes.data && <p className="muted">Loading…</p>}
          {recipes.data?.length === 0 && (
            <p className="muted">
              Compose the things you cook often — they log as one portion instead of five
              ingredients.
            </p>
          )}
          {recipes.data?.map((recipe) => (
            <div className="entry" key={recipe.id}>
              <button
                className="entry__body entry__open"
                onClick={() => navigate(`/recipes/${recipe.id}`)}
              >
                <div className="entry__name">{recipe.name}</div>
                {/* `recipe.food` being null is not a loading state — the server
                    has the recipe and never materialised a food for it, which
                    saving again fixes. Saying so beats a blank number. */}
                <div className="entry__meta num">
                  {recipe.food
                    ? `${Math.round(recipe.food.kcal_per_100)} kcal / 100 g · makes ${
                        recipe.servings_made
                      } · ${recipe.items.length} ingredient${
                        recipe.items.length === 1 ? "" : "s"
                      }`
                    : "needs rebuilding — open and save it"}
                </div>
              </button>
              <span className="foods__chev" aria-hidden="true">
                ›
              </span>
            </div>
          ))}
        </section>
      )}

      <p className="foods__note">
        No <span className="num">+</span> on these rows. Logging needs a meal to land in and this
        screen has none — guessing one is how food ends up in the wrong row.
      </p>
    </div>
  );
}
